import { useEffect, useMemo, useState } from "react";
import { Check, ExternalLink, Link2, RefreshCcw, X } from "lucide-react";
import { useAdapterActions, useAdapters } from "../hooks/useAdapters";
import { useResolvedWorkspace } from "../hooks/useWorkspaces";
import { ApiError } from "../lib/api";
import { WORKSPACE_ID_REGEX } from "../types/api";
import type { AdapterInfo, AdapterType } from "../types/api";

const WORKSPACE_ID = import.meta.env.VITE_DEFAULT_WORKSPACE_ID as string | undefined;

const ADAPTER_ICONS: Record<AdapterType, string> = {
  line: "💬",
  fieldy: "🎙️",
  slack: "💼",
  gmail: "📧",
  kintone: "📊",
  chatwork: "🗨️",
  notion: "📝",
  plaud: "🎧",
  omi: "⌚",
  google: "🔗",
};

function requiresGroupId(type: AdapterType): boolean {
  return type === "slack" || type === "chatwork" || type === "kintone";
}

function requiresCredentialsRef(type: AdapterType): boolean {
  return type === "slack" || type === "chatwork";
}

function extractValidationErrors(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  if (typeof error.data !== "object" || error.data === null) return null;
  const validationErrors = (error.data as { validationErrors?: unknown }).validationErrors;
  if (Array.isArray(validationErrors) && validationErrors.every((msg) => typeof msg === "string")) {
    return validationErrors.join("\n");
  }
  return null;
}

function AdapterRow({
  adapter,
  workspaceId,
}: {
  adapter: AdapterInfo;
  workspaceId: string;
}) {
  const actions = useAdapterActions(workspaceId, adapter.type);
  const [groupId, setGroupId] = useState("");
  const [credentialsRef, setCredentialsRef] = useState("");
  const [allowedIps, setAllowedIps] = useState("");
  const [adapterUrl, setAdapterUrl] = useState("http://localhost:3002");
  const [message, setMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const baseConfig: Record<string, unknown> = useMemo(() => {
    const config: Record<string, unknown> = {};
    if (requiresGroupId(adapter.type) && groupId.trim().length > 0) {
      config.groupId = groupId.trim();
    }
    if (adapter.type === "kintone" && allowedIps.trim().length > 0) {
      config.allowedIps = allowedIps
        .split(",")
        .map((ip) => ip.trim())
        .filter((ip) => ip.length > 0);
    }
    if (adapter.type === "google" && adapterUrl.trim().length > 0) {
      config.adapterUrl = adapterUrl.trim();
    }
    return config;
  }, [adapter.type, allowedIps, groupId, adapterUrl]);

  const runConnect = async (dryRun: boolean) => {
    try {
      setMessage(null);
      setErrorMessage(null);
      await actions.connect(baseConfig, {
        dryRun,
        credentialsRef: credentialsRef.trim() || undefined,
      });
      setMessage(dryRun ? "DRY RUN 成功" : "接続しました");
    } catch (error) {
      const validationErrorMessage = extractValidationErrors(error);
      if (validationErrorMessage) {
        setErrorMessage(validationErrorMessage);
        return;
      }
      setErrorMessage(error instanceof Error ? error.message : String(error));
    }
  };

  const runDisconnect = async (dryRun: boolean) => {
    try {
      setMessage(null);
      setErrorMessage(null);
      await actions.disconnect(dryRun);
      setMessage(dryRun ? "切断 DRY RUN 成功" : "切断しました");
    } catch (error) {
      const validationErrorMessage = extractValidationErrors(error);
      if (validationErrorMessage) {
        setErrorMessage(validationErrorMessage);
        return;
      }
      setErrorMessage(error instanceof Error ? error.message : String(error));
    }
  };

  const isConnected = adapter.status === "connected";

  return (
    <div className="p-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{ADAPTER_ICONS[adapter.type]}</span>
          <div>
            <h3 className="font-medium">{adapter.name}</h3>
            <div className="flex items-center gap-2 text-sm">
              {isConnected ? (
                <>
                  <Check className="h-4 w-4 text-green-600" />
                  <span className="text-green-600">接続中</span>
                </>
              ) : (
                <>
                  <X className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">未接続</span>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          {!isConnected && (
            <button
              onClick={() => runConnect(true)}
              disabled={actions.isConnecting}
              className="rounded-md border px-3 py-1 text-sm hover:bg-muted disabled:opacity-50"
            >
              DRY RUN
            </button>
          )}

          {!isConnected ? (
            <button
              onClick={() => runConnect(false)}
              disabled={actions.isConnecting}
              className="flex items-center gap-2 rounded-md border px-4 py-2 text-sm hover:bg-muted disabled:opacity-50"
            >
              <ExternalLink className="h-4 w-4" />
              接続
            </button>
          ) : (
            <>
              <button
                onClick={() => runDisconnect(true)}
                disabled={actions.isDisconnecting}
                className="rounded-md border px-3 py-1 text-sm hover:bg-muted disabled:opacity-50"
              >
                DRY RUN
              </button>
              <button
                onClick={() => runDisconnect(false)}
                disabled={actions.isDisconnecting}
                className="rounded-md border px-3 py-1 text-sm text-destructive hover:bg-destructive/10 disabled:opacity-50"
              >
                切断
              </button>
            </>
          )}
        </div>
      </div>

      {!isConnected && (
        <div className="mt-4 grid gap-3 rounded-md bg-muted/40 p-4 md:grid-cols-2">
          {requiresGroupId(adapter.type) && (
            <label className="text-sm">
              <div className="mb-1 text-muted-foreground">groupId</div>
              <input
                value={groupId}
                onChange={(e) => setGroupId(e.target.value)}
                className="w-full rounded-md border bg-background px-3 py-2"
                placeholder="CJ_context"
              />
            </label>
          )}

          {requiresCredentialsRef(adapter.type) && (
            <label className="text-sm">
              <div className="mb-1 text-muted-foreground">credentialsRef</div>
              <input
                value={credentialsRef}
                onChange={(e) => setCredentialsRef(e.target.value)}
                className="w-full rounded-md border bg-background px-3 py-2"
                placeholder="env:SLACK_TIER1_SECRET"
              />
            </label>
          )}

          {adapter.type === "kintone" && (
            <label className="text-sm md:col-span-2">
              <div className="mb-1 text-muted-foreground">allowedIps (comma separated)</div>
              <input
                value={allowedIps}
                onChange={(e) => setAllowedIps(e.target.value)}
                className="w-full rounded-md border bg-background px-3 py-2"
                placeholder="192.168.1.1,10.0.0.2"
              />
            </label>
          )}

          {adapter.type === "google" && (
            <label className="text-sm">
              <div className="mb-1 text-muted-foreground">Adapter URL</div>
              <input
                value={adapterUrl}
                onChange={(e) => setAdapterUrl(e.target.value)}
                className="w-full rounded-md border bg-background px-3 py-2"
                placeholder="http://localhost:3002"
              />
            </label>
          )}
        </div>
      )}

      <div className="mt-3 space-y-2 text-sm">
        {adapter.webhookUrl && (
          <div className="flex items-center justify-between rounded-md bg-muted/40 px-3 py-2">
            <span className="text-muted-foreground">Webhook URL</span>
            <code className="rounded bg-background px-2 py-1 text-xs">{adapter.webhookUrl}</code>
          </div>
        )}
        {adapter.lastSync && (
          <div className="flex items-center justify-between rounded-md bg-muted/40 px-3 py-2">
            <span className="text-muted-foreground">最終更新</span>
            <span>{new Date(adapter.lastSync).toLocaleString("ja-JP")}</span>
          </div>
        )}
        {message && <div className="rounded-md bg-green-50 px-3 py-2 text-green-700">{message}</div>}
        {errorMessage && <div className="whitespace-pre-line rounded-md bg-red-50 px-3 py-2 text-red-700">{errorMessage}</div>}
      </div>
    </div>
  );
}

export function AdaptersPage() {
  const resolvedWorkspace = useResolvedWorkspace(WORKSPACE_ID);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState("");

  useEffect(() => {
    if (resolvedWorkspace.workspaceId && resolvedWorkspace.workspaceId !== selectedWorkspaceId) {
      setSelectedWorkspaceId(resolvedWorkspace.workspaceId);
    }
  }, [resolvedWorkspace.workspaceId, selectedWorkspaceId]);

  const workspaceId = selectedWorkspaceId.trim();
  const { data, isLoading, error, refetch, isFetching } = useAdapters(workspaceId);
  const adapters = Array.isArray(data) ? data : [];

  if (resolvedWorkspace.isLoading) {
    return (
      <div className="rounded-lg border bg-card p-4 text-sm text-muted-foreground">
        利用可能なワークスペースを確認しています...
      </div>
    );
  }

  if (resolvedWorkspace.error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        ワークスペース一覧の取得に失敗しました。ログイン状態か API 接続を確認してください。
      </div>
    );
  }

  if (resolvedWorkspace.workspaces.length === 0) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
        利用可能なワークスペースがありません。先にオンボーディングでワークスペースを準備してください。
      </div>
    );
  }

  if (!WORKSPACE_ID_REGEX.test(workspaceId)) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        workspaceId の形式が不正です。英数字で始まる 2〜128 文字の値が必要です。
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">アダプター連携設定</h1>
          <p className="text-muted-foreground">workspace_integrations を使って外部連携を管理</p>
          <p className="text-xs text-muted-foreground">workspaceId: {workspaceId}</p>
          {!resolvedWorkspace.preferredWorkspaceIsValid && WORKSPACE_ID?.trim() && (
            <p className="mt-1 text-xs text-amber-700">
              `VITE_DEFAULT_WORKSPACE_ID` の形式が不正だったため、利用可能なワークスペースへ自動で切り替えています。
            </p>
          )}
          {resolvedWorkspace.preferredWorkspaceId &&
            resolvedWorkspace.preferredWorkspaceIsValid &&
            !resolvedWorkspace.preferredWorkspaceMatched && (
              <p className="mt-1 text-xs text-amber-700">
                指定された `VITE_DEFAULT_WORKSPACE_ID` にアクセスできなかったため、表示可能なワークスペースへ切り替えています。
              </p>
            )}
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm hover:bg-muted disabled:opacity-50"
        >
          <RefreshCcw className="h-4 w-4" />
          再取得
        </button>
      </div>

      <div className="rounded-lg border bg-card shadow-sm">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div>
            <h2 className="font-semibold">アダプター一覧</h2>
            {resolvedWorkspace.workspaces.length > 1 && (
              <label className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                <span>表示ワークスペース</span>
                <select
                  value={workspaceId}
                  onChange={(e) => setSelectedWorkspaceId(e.target.value)}
                  className="rounded-md border bg-background px-2 py-1 text-xs text-foreground"
                >
                  {resolvedWorkspace.workspaces.map((workspace) => (
                    <option key={workspace.id} value={workspace.id}>
                      {workspace.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Link2 className="h-4 w-4" />
            DRY RUN -&gt; CONNECT
          </div>
        </div>

        {isLoading ? (
          <div className="p-6 text-sm text-muted-foreground">読み込み中...</div>
        ) : error ? (
          <div className="p-6 text-sm text-red-600">
            {(error as Error).message}
          </div>
        ) : (
          <div className="divide-y">
            {adapters.map((adapter) => (
              <AdapterRow key={adapter.type} adapter={adapter} workspaceId={workspaceId} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
